import React, { useCallback, useState } from 'react'
import { TouchableOpacity, View } from 'react-native'
import { Popover } from '@ui-kitten/components'
import { useNavigation } from '@react-navigation/native'
import { StackNavigationProp } from '@react-navigation/stack'
import { AppStackParamList } from '@src/navigation/types'
import { Analytics, CustomEventTypes } from '@src/config'
import { Icon, Text } from '@src/components/base'
import { StyleService, useStyleSheet } from '@src/style/service'

interface NotificationInboxMoreMenuProps {
  onMarkAllAsReadPress: () => void
  isMarkAllAsReadDisabled: boolean
}

export const NotificationInboxMoreMenu = ({
  onMarkAllAsReadPress,
  isMarkAllAsReadDisabled,
}: NotificationInboxMoreMenuProps) => {
  const styles = useStyleSheet(themedStyles)
  const navigation = useNavigation<StackNavigationProp<AppStackParamList>>()
  const [menuVisible, setMenuVisible] = useState(false)

  const closeMenu = useCallback(() => {
    setMenuVisible(false)
  }, [])

  const openMenu = useCallback(() => {
    setMenuVisible(true)
  }, [])

  const onNotificationSettingsPress = useCallback(() => {
    Analytics.track(CustomEventTypes.NotificationInboxSettingsTapped)
    closeMenu()
    navigation.navigate('NotificationsSettings')
  }, [closeMenu, navigation])

  const onMarkAllAsReadMenuPress = useCallback(() => {
    Analytics.track(CustomEventTypes.NotificationInboxMarkAllReadTapped)
    closeMenu()
    onMarkAllAsReadPress()
  }, [closeMenu, onMarkAllAsReadPress])

  const renderAnchor = () => (
    <TouchableOpacity
      style={styles.menuButton}
      accessibilityLabel="Notification Inbox more actions"
      onPress={openMenu}
    >
      <Icon name="dots-three-vertical" size={20} weight="bold" style={styles.menuIcon} />
    </TouchableOpacity>
  )

  return (
    <Popover
      anchor={renderAnchor}
      visible={menuVisible}
      onBackdropPress={closeMenu}
      style={styles.popover}
    >
      <View style={styles.container}>
        <TouchableOpacity
          accessibilityLabel="Mark all notifications as read"
          accessibilityState={{ disabled: isMarkAllAsReadDisabled }}
          onPress={onMarkAllAsReadMenuPress}
          style={styles.menuItem}
          disabled={isMarkAllAsReadDisabled}
        >
          <Icon
            name="checks"
            size={20}
            weight="bold"
            style={[styles.menuItemIcon, isMarkAllAsReadDisabled && styles.menuItemIconDisabled]}
          />
          <Text
            type="regular"
            style={[styles.menuItemTitle, isMarkAllAsReadDisabled && styles.menuItemTitleDisabled]}
          >
            Mark All as Read
          </Text>
        </TouchableOpacity>
        <TouchableOpacity
          accessibilityLabel="Notification Settings"
          onPress={onNotificationSettingsPress}
          style={[styles.menuItem, styles.menuItemLast]}
        >
          <Icon name="gear" size={20} weight="bold" style={styles.menuItemIcon} />
          <Text type="regular" style={styles.menuItemTitle}>
            Notification Settings
          </Text>
        </TouchableOpacity>
      </View>
    </Popover>
  )
}

const themedStyles = StyleService.create({
  popover: {
    marginRight: 12,
    borderWidth: 0,
    borderRadius: 14,
    zIndex: 10,
    shadowColor: 'theme.solid.black',
    shadowOffset: {
      width: 0,
      height: 4,
    },
    shadowOpacity: 0.12,
    shadowRadius: 20,
    elevation: 10,
    backgroundColor: 'theme.background',
  },
  container: {
    borderWidth: 1,
    borderRadius: 14,
    borderColor: 'theme.surface.base',
    minWidth: 220,
    overflow: 'hidden',
    backgroundColor: 'theme.background',
  },
  menuIcon: {
    color: 'theme.primary.base',
  },
  menuButton: {
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 4,
    width: 44,
    height: 44,
  },
  menuItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    borderBottomWidth: 1,
    borderBottomColor: 'theme.surface.base2',
    paddingVertical: 10,
    paddingHorizontal: 12,
    backgroundColor: 'theme.background',
  },
  menuItemLast: {
    borderBottomWidth: 0,
  },
  menuItemIcon: {
    color: 'theme.primary.base',
  },
  menuItemTitle: {
    color: 'theme.text.primary',
  },
  menuItemIconDisabled: {
    color: 'theme.disabled.text',
  },
  menuItemTitleDisabled: {
    color: 'theme.disabled.text',
  },
})
